import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

type Brand = {
  id: number;
  name: string;
};

type BannerSlide = {
  id: number;
  brand_id: number;
  title: string;
  subtitle: string | null;
  cta_label: string;
  image_url: string;
  sort_order: number;
  is_active: boolean;
};

const emptyForm: Omit<BannerSlide, "id"> = {
  brand_id: 0,
  title: "",
  subtitle: "",
  cta_label: "Shop Now",
  image_url: "",
  sort_order: 0,
  is_active: true,
};

const getBannerStoragePathFromUrl = (url: string) => {
  if (!url) return null;

  const marker = "/banners/";
  const idx = url.indexOf(marker);
  if (idx === -1) return null;

  const path = url.slice(idx + marker.length);

  return path.split("?")[0] || null;
};

const AdminBannerPage: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [slides, setSlides] = useState<BannerSlide[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<Omit<BannerSlide, "id">>(emptyForm);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data: brandData, error: brandError } = await supabase
          .from("Brand")
          .select("id, name")
          .order("name", { ascending: true });

        if (brandError) throw brandError;
        setBrands(brandData || []);

        const { data: slideData, error: slideError } = await supabase
          .from("BannerSlide")
          .select("*")
          .order("sort_order", { ascending: true });

        if (slideError) throw slideError;
        setSlides(slideData || []);
      } catch (err: any) {
        console.error("❌ Failed to load banner data:", err.message || err);
        setError("Failed to load banner slides or brands.");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      brand_id: brands[0]?.id ?? 0,
    });
  };

  useEffect(() => {
    if (!brands.length) return;
    if (form.brand_id === 0) {
      setForm((prev) => ({ ...prev, brand_id: brands[0].id }));
    }
  }, [brands, form.brand_id]);

  const handleEdit = (slide: BannerSlide) => {
    setEditingId(slide.id);
    setForm({
      brand_id: slide.brand_id,
      title: slide.title,
      subtitle: slide.subtitle ?? "",
      cta_label: slide.cta_label,
      image_url: slide.image_url,
      sort_order: slide.sort_order,
      is_active: slide.is_active,
    });
  };

  const handleDelete = async (slideId: number) => {
    if (!window.confirm("Delete this slide?")) return;

    setSaving(true);
    setError(null);

    try {

      const { data: slide, error: slideErr } = await supabase
        .from("BannerSlide")
        .select("id, image_url")
        .eq("id", slideId)
        .single();

      if (slideErr) throw slideErr;

      const imageUrl = (slide?.image_url || "").trim();

      if (imageUrl) {
        const { data: others, error: othersErr } = await supabase
          .from("BannerSlide")
          .select("id")
          .eq("image_url", imageUrl)
          .neq("id", slideId)
          .limit(1);

        if (othersErr) throw othersErr;

        const isShared = (others || []).length > 0;

        if (!isShared) {
          const storagePath = getBannerStoragePathFromUrl(imageUrl);

          if (storagePath) {
            const { error: removeErr } = await supabase.storage
              .from("banners")
              .remove([storagePath]);


            if (removeErr) throw removeErr;
          } else {
            console.warn(
              "⚠️ Could not parse banners storage path from image_url:",
              imageUrl
            );
          }
        }
      }

      // 3) Delete the DB row
      const { error: deleteError } = await supabase
        .from("BannerSlide")
        .delete()
        .eq("id", slideId);

      if (deleteError) throw deleteError;

      // 4) Update UI
      setSlides((prev) => prev.filter((s) => s.id !== slideId));
      if (editingId === slideId) resetForm();
    } catch (err: any) {
      console.error("❌ Failed to delete slide (and/or image):", err?.message || err);
      setError("Failed to delete slide (and/or image). Check console for details.");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (!form.brand_id) {
        setError("Please select a brand.");
        setSaving(false);
        return;
      }

      if (!form.image_url.trim()) {
        setError("Please upload a banner image.");
        setSaving(false);
        return;
      }

      if (editingId === null) {
        const { data, error: insertError } = await supabase
          .from("BannerSlide")
          .insert([
            {
              brand_id: form.brand_id,
              title: form.title.trim(),
              subtitle: form.subtitle?.trim() || null,
              cta_label: form.cta_label.trim() || "Shop Now",
              image_url: form.image_url.trim(),
              sort_order: form.sort_order,
              is_active: form.is_active,
            },
          ])
          .select("*")
          .single();

        if (insertError) throw insertError;

        setSlides((prev) =>
          [...prev, data as BannerSlide].sort(
            (a, b) => a.sort_order - b.sort_order
          )
        );
        resetForm();
      } else {
        const { data, error: updateError } = await supabase
          .from("BannerSlide")
          .update({
            brand_id: form.brand_id,
            title: form.title.trim(),
            subtitle: form.subtitle?.trim() || null,
            cta_label: form.cta_label.trim() || "Shop Now",
            image_url: form.image_url.trim(),
            sort_order: form.sort_order,
            is_active: form.is_active,
          })
          .eq("id", editingId)
          .select("*")
          .single();

        if (updateError) throw updateError;

        setSlides((prev) =>
          prev
            .map((s) => (s.id === editingId ? (data as BannerSlide) : s))
            .sort((a, b) => a.sort_order - b.sort_order)
        );
        resetForm();
      }
    } catch (err: any) {
      console.error("❌ Failed to save slide:", err.message || err);
      setError("Failed to save slide.");
    } finally {
      setSaving(false);
    }
  };

  const getBrandName = (brandId: number) =>
    brands.find((b) => b.id === brandId)?.name || `Brand #${brandId}`;

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `banner-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("banners")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from("banners").getPublicUrl(filePath);
      const publicUrl = data.publicUrl;

      setForm((prev) => ({ ...prev, image_url: publicUrl }));
    } catch (err: any) {
      console.error("❌ Failed to upload image:", err.message || err);
      setError("Failed to upload banner image.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-[#5c2f2f] mb-6">
        Banner Slides (Homepage Slider)
      </h1>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 text-red-700 px-4 py-2 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-[#7a4343]">Loading brands and slides...</p>
      ) : (
        <>
          <div className="mb-10 bg-white/70 backdrop-blur-sm border border-[#f2cccc] rounded-2xl p-6 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-[#5c2f2f]">
                {editingId ? "Edit Slide" : "Create New Slide"}
              </h2>
              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="text-sm text-[#a25d5d] hover:text-[#7a4343] underline"
                >
                  Cancel edit
                </button>
              )}
            </div>

            <form
              onSubmit={handleSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <div className="flex flex-col text-left">
                <label className="text-sm font-medium text-[#5c2f2f] mb-1">
                  Brand
                </label>
                <select
                  value={form.brand_id || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      brand_id: Number(e.target.value),
                    }))
                  }
                  className="border border-[#f2cccc] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#f2cccc]"
                >
                  <option value="">Select a brand</option>
                  {brands.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col text-left">
                <label className="text-sm font-medium text-[#5c2f2f] mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, title: e.target.value }))
                  }
                  className="border border-[#f2cccc] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#f2cccc]"
                  placeholder="e.g. Strength Cure Repair"
                />
              </div>

              <div className="flex flex-col text-left">
                <label className="text-sm font-medium text-[#5c2f2f] mb-1">
                  Subtitle (optional)
                </label>
                <input
                  type="text"
                  value={form.subtitle ?? ""}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, subtitle: e.target.value }))
                  }
                  className="border border-[#f2cccc] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#f2cccc]"
                  placeholder="e.g. Repair and protect damaged hair"
                />
              </div>

              <div className="flex flex-col text-left">
                <label className="text-sm font-medium text-[#5c2f2f] mb-1">
                  Button Text
                </label>
                <input
                  type="text"
                  value={form.cta_label}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, cta_label: e.target.value }))
                  }
                  className="border border-[#f2cccc] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#f2cccc]"
                  placeholder="Shop Now"
                />
              </div>

              <div className="flex flex-col text-left md:col-span-2">
                <label className="text-sm font-medium text-[#5c2f2f] mb-1">
                  Banner Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  className="block w-full text-sm text-[#5c2f2f]
                             file:mr-4 file:py-2 file:px-4
                             file:rounded-full file:border-0
                             file:text-sm file:font-semibold
                             file:bg-[#fbeaea] file:text-[#5c2f2f]
                             hover:file:bg-[#f5d7d7]"
                />
                <p className="text-xs text-[#7a4343] mt-1">
                  Upload a banner image for this slide. Bucket used:{" "}
                  <span className="font-semibold">banners</span>.
                </p>

                {uploading && (
                  <p className="text-xs text-[#7a4343] mt-1">
                    Uploading image...
                  </p>
                )}

                {form.image_url && (
                  <div className="mt-3 flex items-center gap-4">
                    <img
                      src={form.image_url}
                      alt="Banner preview"
                      className="w-40 h-24 object-cover rounded-lg border border-[#f2cccc]"
                    />
                    <p className="text-xs text-[#7a4343] break-all text-left">
                      {form.image_url}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex flex-col text-left">
                <label className="text-sm font-medium text-[#5c2f2f] mb-1">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      sort_order: Number(e.target.value),
                    }))
                  }
                  className="border border-[#f2cccc] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#f2cccc]"
                  placeholder="0, 1, 2..."
                />
              </div>

              <div className="flex items-center gap-2 text-left">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      is_active: e.target.checked,
                    }))
                  }
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium text-[#5c2f2f]">
                  Active (visible on homepage)
                </span>
              </div>

              <div className="md:col-span-2 flex justify-end mt-4">
                <button
                  type="submit"
                  disabled={saving || uploading}
                  className="px-6 py-2 rounded-full bg-[#5c2f2f] text-white font-semibold hover:bg-[#7a4343] transition disabled:opacity-60"
                >
                  {saving
                    ? editingId
                      ? "Saving..."
                      : "Creating..."
                    : editingId
                    ? "Save Changes"
                    : "Create Slide"}
                </button>
              </div>
            </form>
          </div>

          <div className="bg-white/70 backdrop-blur-sm border border-[#f2cccc] rounded-2xl p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-[#5c2f2f] mb-4 text-left">
              Existing Slides
            </h2>

            {slides.length === 0 ? (
              <p className="text-[#7a4343] text-sm">
                No slides yet. Create one using the form above.
              </p>
            ) : (
              <div className="space-y-4">
                {slides.map((slide) => (
                  <div
                    key={slide.id}
                    className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border border-[#f6dede] rounded-xl p-4 bg-[#fff8f8]"
                  >
                    <div className="flex items-start gap-4">
                      {slide.image_url && (
                        <img
                          src={slide.image_url}
                          alt={slide.title}
                          className="w-32 h-20 object-cover rounded-lg border border-[#f2cccc]"
                        />
                      )}
                      <div className="text-left">
                        <p className="text-sm font-semibold text-[#5c2f2f]">
                          {slide.title}
                        </p>
                        {slide.subtitle && (
                          <p className="text-xs text-[#7a4343]">
                            {slide.subtitle}
                          </p>
                        )}
                        <p className="text-xs text-[#7a4343] mt-1">
                          Brand:{" "}
                          <span className="font-semibold">
                            {getBrandName(slide.brand_id)}
                          </span>
                        </p>
                        <p className="text-xs text-[#7a4343]">
                          CTA:{" "}
                          <span className="font-semibold">{slide.cta_label}</span>{" "}
                          · Order: {slide.sort_order} ·{" "}
                          {slide.is_active ? (
                            <span className="text-green-700 font-semibold">
                              Active
                            </span>
                          ) : (
                            <span className="text-gray-500">Inactive</span>
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3 self-end md:self-center">
                      <button
                        onClick={() => handleEdit(slide)}
                        className="text-sm px-3 py-1 rounded-full border border-[#f2cccc] text-[#5c2f2f] hover:bg-[#fbeaea] transition"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(slide.id)}
                        disabled={saving}
                        className="text-sm px-3 py-1 rounded-full border border-red-200 text-red-700 hover:bg-red-50 transition disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminBannerPage;
